/**
 * 公式の地区割当データの読み込み (fetch + メモリキャッシュ + inflight dedup)。
 * 生成: scripts/import-tokyo-assignment.ts / scripts/import-yokohama-assignment.ts
 * 読めないファイルは空として扱う — 割当は自動選定の精度を上げる追加段で、
 * 無くても従来の自動選定で下書きは作れる (districtStay.ts と同じ方針)。
 * ただし読めなかった回はキャッシュしない (次の呼び出しで読み直す)。
 */
import type { AssignmentData, TownTable, ZoneSet } from '@/lib/evacuation-sites/official-assignment';

/** 面の割当 (public/ 配下)。スクリプト (scripts/lib/master-data.ts) も同じ一覧を読む。 */
export const ZONE_URLS = ['/data/evacuation-assignment.tokyo.json'];
/** 町丁目表の割当 (public/ 配下)。 */
export const TOWN_URLS = ['/data/evacuation-assignment.yokohama.json'];

let cache: AssignmentData | null = null;
let inflight: Promise<AssignmentData> | null = null;

async function readJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as T;
  } catch (e) {
    console.warn(`[evacuationAssignment] ${url} を読めませんでした (割当なしで自動選定します)`, e);
    return null;
  }
}

/**
 * 読んだファイル (読めなければ null) を AssignmentData にまとめる (純関数)。
 * 形の壊れたファイルは落とす。scripts/ からも同じ変換で読む。
 */
export function toAssignmentData(
  zones: Array<ZoneSet | null>,
  towns: Array<TownTable | null>,
): AssignmentData {
  return {
    zoneSets: zones
      .filter((z): z is ZoneSet => !!z && Array.isArray(z.zones))
      .map((z) => ({ basis: z.basis, zones: z.zones })),
    townTables: towns
      .filter((t): t is TownTable => !!t && Array.isArray(t.entries))
      .map((t) => ({
        city: t.city,
        basis: t.basis,
        entries: t.entries,
        ...(t.unassignedLabel ? { unassignedLabel: t.unassignedLabel } : {}),
      })),
  };
}

export async function loadAssignmentData(): Promise<AssignmentData> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const [zones, towns] = await Promise.all([
        Promise.all(ZONE_URLS.map((u) => readJson<ZoneSet>(u))),
        Promise.all(TOWN_URLS.map((u) => readJson<TownTable>(u))),
      ]);
      const data = toAssignmentData(zones, towns);
      // 1 つでも読めなかったら、読めた分を返すだけでキャッシュしない。一時的な通信の
      // 失敗をキャッシュすると、再読み込みまでその地域がずっと「割当なし」になる。
      if (data.zoneSets.length === ZONE_URLS.length && data.townTables.length === TOWN_URLS.length) {
        cache = data;
      }
      return data;
    } catch (e) {
      console.warn(
        '[evacuationAssignment] 割当データの読み込みに失敗しました (割当なしで自動選定します)',
        e,
      );
      return { zoneSets: [], townTables: [] };
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** テスト用: キャッシュを破棄する。 */
export function __resetAssignmentCache(): void {
  cache = null;
  inflight = null;
}
